import { uiText, useUiLanguage, currentLocale } from '@/i18n/interface';
import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrentDojo } from '@/hooks/useCurrentDojo';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, CheckCircle, Clock, Crown, Sparkles } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

const TrialExpiredScreen: React.FC = () => {
  useUiLanguage();
  const { profile } = useAuth();
  const { dojo } = useCurrentDojo();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubscribe = () => {
    if (!profile?.id) {
      toast({
        title: uiText("Authentication Required"),
        description: uiText("Please log in to continue"),
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);

    // Open the direct Stripe checkout link
    window.open('https://buy.stripe.com/fZu5kD06o4U37Ha8WTdAk00', '_blank');

    // Reset loading state after a short delay
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  const formatTrialStart = (trialStart: string) => {
    const date = new Date(trialStart);
    return date.toLocaleDateString(currentLocale(), {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTrialEndDate = (trialStart: string) => {
    const start = new Date(trialStart);
    const end = new Date(start.getTime() + (14 * 24 * 60 * 60 * 1000));
    return end.toLocaleDateString(currentLocale(), {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <Card className="border-2 border-destructive/20 shadow-2xl">
          <CardHeader className="text-center pb-6">
            <div className="flex justify-center mb-4">
              <div className="relative">
                <Clock className="h-16 w-16 text-destructive" />
                <div className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1">
                  <CheckCircle className="h-4 w-4" />
                </div>
              </div>
            </div>
            <CardTitle className="text-3xl font-bold text-destructive mb-2">
              {uiText("Account Locked - Trial Expired")}</CardTitle>
            <p className="text-muted-foreground text-lg">
              {uiText("Your 14-day free trial for")}{' '}<strong>{dojo?.name}</strong> {' '}{uiText("has ended. Your account is now locked until you subscribe.")}</p>
          </CardHeader>

          <CardContent className="space-y-8">
            {/* Trial Information */}
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <h3 className="font-semibold">{uiText("Trial Period")}</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{uiText("Started:")}</span>
                  <span>{dojo?.trial_start ? formatTrialStart(dojo.trial_start) : uiText("Unknown")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{uiText("Ended:")}</span>
                  <span>{dojo?.trial_start ? getTrialEndDate(dojo.trial_start) : uiText("Unknown")}</span>
                </div>
              </div>
            </div>

            {/* Pro Plan Features */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Crown className="h-5 w-5 text-primary" />
                <h3 className="text-xl font-semibold">{uiText("Continue with Professional Plan")}</h3>
                <Badge className="bg-primary text-primary-foreground">
                  {uiText("Recommended")}</Badge>
              </div>

              <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-2xl font-bold">$49.99</h4>
                    <p className="text-muted-foreground">{uiText("per month")}</p>
                  </div>
                  <Sparkles className="h-8 w-8 text-primary" />
                </div>

                <ul className="space-y-3">
                  {[
                    'Unlimited students and training sessions',
                    'Advanced attendance tracking',
                    'Payment management system',
                    'Belt promotion tracking',
                    'Live chat with students',
                    'Warrior AI assistant',
                    'On-demand training content',
                    'Detailed analytics and reports',
                    'Priority support'
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                      <span className="text-sm">{uiText(feature)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                onClick={handleSubscribe}
                disabled={isLoading}
                className="w-full h-12 text-lg bg-primary hover:bg-primary/90"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    {uiText("Processing...")}</>
                ) : (
                  <>
                    <Crown className="h-5 w-5 mr-2" />
                    {uiText("Unlock Account - Subscribe Now")}</>
                )}
              </Button>

              <div className="text-center">
                <p className="text-xs text-muted-foreground">
                  {uiText("Secure payment processing by Stripe • Cancel anytime")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TrialExpiredScreen;
